<script setup lang="ts">
import DownArrowSvg from './visualAssets/DownArrow.svg?component';
import UpArrowSvg from './visualAssets/UpArrow.svg?component';
import LevelArrowSvg from './visualAssets/LevelArrow.svg?component';
import { useScreen } from '@Shared/utils/useScreen';
import { calculateDeltaValue, calculateMetricValue, DeltaType, MetricType, formatDeltaValue } from './utility';
import { roundAwayFromZero } from '@Shared/utils/util';
import { MeasureResult } from '@Shared/utils/types';
import { useResizeObserver } from '@Shared/utils/useResizeObserver';

const props = defineProps<{
  title: string;
  measureResults?: MeasureResult[];
}>();

const max = ref(10);
const gridEl = ref<HTMLElement | null>(null);
const resize = async () => {
  if (gridEl.value) {
    const height =
      gridEl.value.parentElement!.parentElement!.clientHeight -
      gridEl.value.parentElement!.firstElementChild!.clientHeight -
      20; // just a bit extra for the padding
    const heightOneChild = gridEl.value.firstElementChild!.clientHeight;

    // -1 since the "Driver with the lowest score"
    const numberOfChildren = Math.max(3, Math.min(10, Math.floor(height / heightOneChild) - 1));

    max.value = numberOfChildren;
  }
};

const screenSize = useScreen(resize);
useResizeObserver(gridEl, resize, true);

const sortedDrivers = computed<MeasureResult[] | undefined>(() => {
  const results = props.measureResults
    ?.slice(0)
    .filter((f) => f.currentValue !== null && f.currentValue !== undefined)
    .sort((a, b) => b.currentValue! - a.currentValue!);
  if (!results) return undefined;

  if (results.length > max.value) {
    // don't remove the last one
    // i.e. [1, 2, 3, 4, 5, 6] with max = 5 will be splice(5 - 1, 1)
    // which will result in [1, 2, 3, 4, 6]
    results.splice(results.length - 1 - (results.length - max.value), results.length - max.value);
  }

  return results;
});

const formatValue = (value?: number): string => {
  return calculateMetricValue(value, MetricType.Nps);
};

const calculateAndFormatDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);
  return formatDeltaValue(deltaValue, DeltaType.PointDelta);
};

const getWidthForDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);

  let scale = 0.75;
  if (screenSize.value.width >= 3840) {
    scale = 1.5;
  } else if (screenSize.value.width >= 1920) {
    scale = 1;
  }

  if (deltaValue == null || deltaValue == 0) return `${25 * scale}px`;
  return `${20 * scale}px`;
};

const getMaxWidthForDeltaValue = (): string => {
  let scale = 0.75;
  if (screenSize.value.width >= 3840) {
    scale = 1.5;
  } else if (screenSize.value.width >= 1920) {
    scale = 1;
  }

  return `${30 * scale}px`;
};

const getColorForDeltaValue = (driver: MeasureResult): string => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);

  if (deltaValue == null) return '#7F7466';
  if (deltaValue < 0) return '#D4002B';
  if (deltaValue == 0) return '#7F7466';
  if (deltaValue > 0) return '#0C8A65';

  return '';
};

const deltaValueImg = (driver: MeasureResult) => {
  const deltaValue = calculateDeltaValue(driver, DeltaType.PointDelta);
  if (deltaValue == null) return null;

  if (roundAwayFromZero(deltaValue * 100) < 0) {
    return DownArrowSvg;
  } else if (roundAwayFromZero(deltaValue * 100) > 0) {
    return UpArrowSvg;
  } else {
    return LevelArrowSvg;
  }
};
</script>
<template>
  <div class="h-full flex flex-col bg-[#F9FAFB] rounded-[8px] pb-4 2xl:pb-0">
    <div
      class="w-full truncate font-bold xl:min-h-4 3xl:min-h-12 xl:text-4 2xl:text-5.5 3xl:text-11 pt-3 pb-3 flex-[0_1_auto] pl-6"
    >
      {{ title }}
    </div>
    <div class="flex flex-col flex-[1_1_auto] h-full 2xl:h-8" ref="gridEl">
      <div
        class="grid items-center pl-6 pr-4 3xl:pl-12"
        :style="{
          'grid-template-columns': `66% 12% ${getMaxWidthForDeltaValue()} auto`,
          gap: '0.25rem',
        }"
        :class="{ 'mb-2': i == sortedDrivers!.length - 1 }"
        v-for="(driver, i) in sortedDrivers!.slice(0, -1)"
        :key="driver.label"
      >
        <div class="text-[#333333] truncate leading-normal font-semibold xl:text-4 2xl:text-5 3xl:text-10">
          {{ driver.label }}
        </div>
        <div class="justify-self-end xl:text-3.5 2xl:text-4 3xl:text-8 font-bold">
          {{ formatValue(driver.currentValue) }}
        </div>
        <div class="justify-self-end self-center">
          <component
            v-if="driver.previousValue != null"
            :width="getWidthForDeltaValue(driver)"
            :is="deltaValueImg(driver)"
          />
        </div>
        <div
          class="justify-self-start xl:text-3.5 2xl:text-4 3xl:text-8"
          :style="{ color: getColorForDeltaValue(driver) }"
        >
          {{ calculateAndFormatDeltaValue(driver) }}
        </div>
      </div>
      <div class="border-t border-[#BDBDBD] mt-auto mb-auto w-full 2xl:mt-5 2xl:mb-0"></div>
      <div
        v-if="sortedDrivers && sortedDrivers.length > 1"
        class="font-bold truncate leading-normal mt-2 xl:text-4 2xl:text-5.5 3xl:text-10 text-[#333333] pl-6 2xl:mt-0 3xl:pl-12"
      >
        Driver with the lowest score
      </div>
      <div
        class="grid items-center pl-6 pr-4 3xl:pl-12"
        :style="{
          'grid-template-columns': `66% 12% ${getMaxWidthForDeltaValue()} auto`,
          gap: '0.25rem',
        }"
        v-if="sortedDrivers && sortedDrivers.length > 1"
      >
        <div class="text-[#333333] truncate leading-normal font-semibold xl:text-4 2xl:text-5 3xl:text-10">
          {{ sortedDrivers[sortedDrivers.length - 1].label }}
        </div>
        <div class="justify-self-end xl:text-3.5 2xl:text-4 3xl:text-8 font-bold">
          {{ formatValue(sortedDrivers[sortedDrivers.length - 1].currentValue) }}
        </div>
        <div class="justify-self-end self-center text-left">
          <component
            v-if="sortedDrivers[sortedDrivers.length - 1].previousValue != null"
            :width="getWidthForDeltaValue(sortedDrivers[sortedDrivers.length - 1])"
            :is="deltaValueImg(sortedDrivers[sortedDrivers.length - 1])"
          />
        </div>
        <div
          class="justify-self-start xl:text-3.5 2xl:text-4 3xl:text-8"
          :style="{
            color: getColorForDeltaValue(sortedDrivers[sortedDrivers.length - 1]),
          }"
        >
          {{ calculateAndFormatDeltaValue(sortedDrivers[sortedDrivers.length - 1]) }}
        </div>
      </div>
    </div>
  </div>
</template>
